import { PrepareAction, createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Sector, Split } from "../../models/Common";
import { RootState, store } from "../reducers/store";
import { LeoService } from "../../services/leoService";
import { FundAffectation, PortfolioAffectation, PortfolioCreateItem, PortfolioIncorporation, PortfolioStatus } from "../../models/Portfolio";
import { Utilities } from "../../utility/utils";
import { updateFilters } from "./libraryOfFunds";
import { updaterCounter } from "./portfolioLibrary";

export const loadSectors = createAsyncThunk<Sector[]>(
    'createPortfolio/loadSectors',
    async () => {
        const leoService = new LeoService();
        return await leoService.GetSectors();
    }
);

export const loadSplits = createAsyncThunk<Split[]>(
    'createPortfolio/loadSplits',
    async () => {
        const leoService = new LeoService();
        return await leoService.GetSplits();
    }
);

export const initEmptyCreation = createAction('createPortfolio/initEmptyCreation');

export const initWithFund = createAsyncThunk<{id: string, fundId: number}, number>(
    'createPortfolio/initWithFund',
    async (fundId: number) => {
        const leoService = new LeoService();
        const id = await leoService.CreatePortfolio("New porfolio");
        await leoService.AddFundToPortfolio(id, fundId);
        return { id: id, fundId: fundId };
    }
);

export const initEdit = createAsyncThunk<PortfolioCreateItem, string>(
    'createPortfolio/initEdit',
    async (id: string) => {
        const leoService = new LeoService();
        return await leoService.GetEditPortfolio(id);
    }
);

export const updatePortfolio = createAction<PrepareAction<{ field: string, value: any }>, string>('createPortfolio/updatePortfolio', (payload: { field: string, value: any }) => {
    delaySaving()
    return { payload: payload };
});

export const ensureSectors = createAction<string[]>('createPortfolio/ensureSectors');
export const ensureFunds = createAsyncThunk<FundAffectation[]>(
    'createPortfolio/ensureFunds',
    async (a, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const leoService = new LeoService();
        const toRemove: number[] = state.createPortfolio.portfolio.funds.filter(f => f.proportion === 0).map(f => f.id);
        for(const id of toRemove){
            await leoService.RemoveFundFromPortfolio(state.createPortfolio.portfolio.id as string, id);
        }
        return state.createPortfolio.portfolio.funds.filter(f => toRemove.findIndex(r => r === f.id) === -1);
    }
);

export const addFundToPortfolio = createAsyncThunk<number, number>('createPortfolio/addFundToPortfolio',
    async (fundId: number, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const leoService = new LeoService();
        await leoService.AddFundToPortfolio(state.createPortfolio.portfolio.id as string, fundId);
        return fundId;
    }
);

export const removeFundToPortfolio = createAsyncThunk<number, number>('createPortfolio/removeFundToPortfolio',
    async (fundId: number, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const leoService = new LeoService();
        await leoService.RemoveFundFromPortfolio(state.createPortfolio.portfolio.id as string, fundId);
        return fundId;
    }
);

export const updateFundOfPortfolio = createAsyncThunk<FundAffectation, FundAffectation>('createPortfolio/updateFundOfPortfolio',
    async (affectation: FundAffectation, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        let finalAffectation = affectation.proportion;
        const total = state.createPortfolio.portfolio.funds.reduce((a,b) => a + (b.id !== affectation.id ? b.proportion : 0), 0);
        if (total + finalAffectation > 1){
            finalAffectation = 1-total;
        }
        const leoService = new LeoService();
        leoService.UpdateFundOfPortfolio(state.createPortfolio.portfolio.id as string, { id: affectation.id, proportion: Utilities.round(finalAffectation,4)});
        return { id: affectation.id, proportion: Utilities.round(finalAffectation,4)};
    }
);

let savingTimeOutId: NodeJS.Timeout | undefined = undefined;

const askExistConfirm = () => {
    return "You have unsaved changes pending, are you sure you want to leave? you'll lose unsaved data!";
}

const delaySaving = async () => {
    if (savingTimeOutId) {
        clearTimeout(savingTimeOutId);
    }
    window.onbeforeunload = askExistConfirm;
    await new Promise(resolve => savingTimeOutId = setTimeout(resolve, 3000));
    savingTimeOutId = undefined;
    window.onbeforeunload = () => { };
    store.dispatch(saveDraft());
}

export const saveDraft = createAsyncThunk<string | undefined>('createPortfolio/updatePortfolio',
    async (a, thunkAPI) => {
        if (savingTimeOutId) {
            savingTimeOutId = undefined;
            window.onbeforeunload = () => { };
        }
        const state = thunkAPI.getState() as RootState;
        if (state.createPortfolio.portfolio.name === ""){
            return;
        }
        const leoService = new LeoService();
        if (state.createPortfolio.portfolio.id !== undefined) {
            await leoService.UpdatePortfolio(state.createPortfolio.portfolio);
            return state.createPortfolio.portfolio.id;
        } else {
            const id = await leoService.CreatePortfolio(state.createPortfolio.portfolio.name as string);
            thunkAPI.dispatch(updaterCounter({status: PortfolioStatus.Draft, change: 1}))
            await leoService.UpdatePortfolio({...state.createPortfolio.portfolio, id: id});
            return id;
        }
    });

export const submitPortfolio = createAsyncThunk('createPortfolio/submitPortfolio',
    async (a, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const leoService = new LeoService();
        if (state.createPortfolio.portfolio.id !== undefined) {
            await leoService.SubmitPortoflio(state.createPortfolio.portfolio.id);
            
            thunkAPI.dispatch(updaterCounter({status: PortfolioStatus.Draft, change: -1}));
            thunkAPI.dispatch(updaterCounter({status: PortfolioStatus.Pending, change: 1}));
        }
    });

export const setDraggingStatus = createAction<boolean>('createPortfolio/setDraggingStatus');

export const affectPortfolio = createAsyncThunk<PortfolioAffectation, PortfolioAffectation>(
    'createPortfolio/affectPortfolio',
    async (affectation, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const leoService = new LeoService();
        await leoService.AffectPortfolio(state.createPortfolio.portfolio.id as string, affectation);
        thunkAPI.dispatch(updateFilters({
            area: state.createPortfolio.portfolio.splits.map(s => s.name),
            sector: state.createPortfolio.portfolio.sectors.map(s => s.name),
            stage: Object.keys(state.createPortfolio.portfolio.stage).filter(s => (state.createPortfolio.portfolio.stage as any)[s] > 0).map(s => Utilities.toUpperCamelCase(s))
        }));
        return affectation;
    }
);

export const incorporatePortfolio = createAsyncThunk<PortfolioIncorporation, PortfolioIncorporation>(
    'createPortfolio/incorporatePortfolio',
    async (incorporation, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const leoService = new LeoService();
        await leoService.IncorporatePortfolio(state.createPortfolio.portfolio.id as string, incorporation);
        return incorporation;
    }
);

export const setShowAllocationError = createAction<boolean>('createPortfolio/setShowAllocationError');

export const dismissAi = createAction<boolean>('createPortfolio/dismissAi')